/**
 * Restores "Load more" pagination state + scroll position on browser Back,
 * for Shopify collection pages using the Section Rendering API.
 *
 * - Saves state to sessionStorage when a product link is clicked.
 * - On return (pageshow), re-fetches the pages that were loaded, appends them,
 *   syncs the Load More component, then scrolls to the clicked product card.
 * - Does NOT touch the visible URL. A hard refresh resets to page 1.
 *
 * Confirm the CONFIG selectors match your theme's HTML before using.
 */
(function () {
  'use strict';

  var CONFIG = {
    gridSelector: '#product-grid',
    cardSelector: '.grid__item',
    componentSelector: 'infinite-scroll',
    nextPageIndicatorSelector: '.check_infinite_scroll',
    sectionId: null, // set manually only if auto-detect fails

    maxAgeMs: 30 * 60 * 1000,
    anchorPosition: 'center', // 'center' or anything else = 80px from top
    storageKey: 'collectionScrollState'
  };

  if (window.Shopify && window.Shopify.designMode) return;

  var grid = document.querySelector(CONFIG.gridSelector);
  if (!grid) return;

  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  // Number of pages currently shown. In memory, resets on every fresh load.
  var pagesLoaded = 1;

  // ---------------------------------------------------------------
  // 1. Track Load More clicks
  // ---------------------------------------------------------------
  document.addEventListener(
    'click',
    function (event) {
      var comp = event.target.closest(CONFIG.componentSelector);
      if (!comp) return;
      var n = parseInt(comp.dataset.title, 10);
      if (!isNaN(n)) pagesLoaded = Math.max(pagesLoaded, n + 1);
    },
    true
  );

  // ---------------------------------------------------------------
  // 2. Save state when a product link is clicked
  // ---------------------------------------------------------------
  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest('a[href*="/products/"]');
      if (!link || !grid.contains(link)) return;

      // Fallback for infinite scroll that loads without a click:
      // the component's data-title reflects how many pages are loaded.
      var comp = document.querySelector(CONFIG.componentSelector);
      if (comp) {
        var t = parseInt(comp.dataset.title, 10);
        if (!isNaN(t)) pagesLoaded = Math.max(pagesLoaded, t);
      }

      try {
        sessionStorage.setItem(
          CONFIG.storageKey,
          JSON.stringify({
            path: location.pathname + location.search,
            y: window.scrollY,
            target: link.getAttribute('href').split('?')[0],
            pageCount: pagesLoaded,
            savedAt: Date.now()
          })
        );
      } catch (e) {
        console.error('collection-restore: save state failed', e);
      }
    },
    true
  );

  // ---------------------------------------------------------------
  // 3. Restore on return
  // ---------------------------------------------------------------
  window.addEventListener('pageshow', function (event) {
    // bfcache restore: browser already kept the page and scroll as-is.
    if (event.persisted) return;

    var state = readState();
    if (!state) return;

    // Stale or for a different page: nothing to restore.
    if (
      Date.now() - state.savedAt > CONFIG.maxAgeMs ||
      location.pathname + location.search !== state.path
    ) {
      clearState();
      return;
    }

    // Only page 1 was loaded: just scroll back.
    if (!state.pageCount || state.pageCount <= 1) {
      anchor(state);
      clearState();
      return;
    }

    refillViaParallelFetch(state).then(function (result) {
      var nextUrl = result ? result.nextUrl : null;
      var pageNumber = result ? result.pageNumber : null;

      pagesLoaded = state.pageCount;

      syncLoadMoreButton(nextUrl, pageNumber);
      anchor(state);
      clearState(); // clear only after products are back in the DOM
    });
  });

  function refillViaParallelFetch(state) {
    var sectionId = resolveSectionId();
    if (!sectionId) return Promise.resolve(null);

    var pageNumbers = [];
    for (var p = 2; p <= state.pageCount; p++) pageNumbers.push(p);

    var requests = pageNumbers.map(function (page) {
      var url = new URL(state.path, location.origin);
      url.searchParams.set('page', page);
      url.searchParams.set('section_id', sectionId);

      return fetch(url.toString())
        .then(function (res) {
          return res.ok ? res.text() : null;
        })
        .then(function (html) {
          if (!html) {
            return { cards: [], hasNext: false, nextUrl: null, pageNumber: null };
          }
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var incoming = doc.querySelectorAll(
            CONFIG.gridSelector + ' ' + CONFIG.cardSelector
          );
          var nextIndicator = doc.querySelector(CONFIG.nextPageIndicatorSelector);
          return {
            pageNumber: nextIndicator ? nextIndicator.dataset.title : null,
            cards: Array.from(incoming),
            hasNext: !!nextIndicator,
            nextUrl: nextIndicator ? nextIndicator.dataset.url : null
          };
        })
        .catch(function () {
          return { cards: [], hasNext: false, nextUrl: null, pageNumber: null };
        });
    });

    // Promise.all keeps result order, so cards append in the right sequence.
    return Promise.all(requests).then(function (results) {
      results.forEach(function (result) {
        result.cards.forEach(function (card) {
          grid.appendChild(document.importNode(card, true));
        });
      });

      var last = results[results.length - 1];
      return {
        nextUrl: last && last.hasNext ? last.nextUrl : null,
        pageNumber: last ? last.pageNumber : null
      };
    });
  }

  function syncLoadMoreButton(nextUrl, pageNumber) {
    var btn = document.querySelector(CONFIG.componentSelector);
    if (!btn) return;

    if (nextUrl) {
      btn.setAttribute('data-url', nextUrl);
      if (pageNumber != null) btn.setAttribute('data-title', pageNumber);
    } else {
      btn.remove();
    }
  }

  function readState() {
    try {
      return JSON.parse(sessionStorage.getItem(CONFIG.storageKey) || 'null');
    } catch (e) {
      return null;
    }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(CONFIG.storageKey);
    } catch (e) {}
  }

  function resolveSectionId() {
    if (CONFIG.sectionId) return CONFIG.sectionId;
    var section = grid.closest('[id^="shopify-section-"]');
    if (section) return section.id.replace('shopify-section-', '');
    var el = grid.closest('[data-section-id]');
    return el ? el.dataset.sectionId : null;
  }

  function anchor(state) {
    var card = null;

    if (state.target) {
      var links = grid.querySelectorAll('a[href*="/products/"]');
      for (var i = 0; i < links.length; i++) {
        if (links[i].getAttribute('href').split('?')[0] === state.target) {
          card = links[i].closest(CONFIG.cardSelector) || links[i];
          break;
        }
      }
    }

    var apply = function () {
      if (card) {
        var rect = card.getBoundingClientRect();
        var offset =
          CONFIG.anchorPosition === 'center'
            ? window.innerHeight / 2 - rect.height / 2
            : 80;
        window.scrollTo(0, Math.max(rect.top + window.scrollY - offset, 0));
      } else {
        window.scrollTo(0, state.y);
      }
    };

    // Re-apply a few times so late-loading images don't shift the position.
    apply();
    requestAnimationFrame(apply);
    setTimeout(apply, 150);
    setTimeout(apply, 400);
    setTimeout(apply, 900);

    if (document.readyState !== 'complete') {
      window.addEventListener('load', apply, { once: true });
    }
  }
})();